import React from 'react';
import './JournalImbalanceCard.css';

/**
 * A journal day's imbalance, its cause, and what the source document says.
 *
 * The layout follows the diagnosis's own hierarchy rather than the page's: the
 * residual, then the cause that closes it, then the document that confirms it.
 * Unmapped accounts appear LAST and are labelled as context — on the page they
 * sit beside the imbalance badge, which invites the reader to treat them as the
 * reason, and they are not.
 */

const money = (value, currency = 'USD') => {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value);
};

// Cause kinds come from the diagnosis model (azfApi journal_imbalance
// CAUSE_KINDS). Older artifacts carry the legacy keys; both are labelled.
const CAUSE_LABEL = {
  wrong_side: 'Posted on the wrong side',
  wrong_amount: 'Posted with the wrong amount',
  missing_line: 'A reported line never arrived',
  duplicate_import: 'The day appears to have been imported twice',
  partial_import: 'The day appears to be partially imported',
  source_unreadable: 'The night audit could not be read reliably',
  multiple: 'Several lines explain the imbalance',
  unexplained: 'Not explained by the source',
  feed_convention: 'The journal feed never balances — import convention, not this day',
  // legacy
  sign_flip: 'Posted on the wrong side',
  source_side_error: 'Posted on the wrong side',
  source_missing_line: 'A reported line never arrived',
  missing_counterpart: 'A counterpart is missing',
  source_discrepancy: 'The journal and the document disagree',
  coordinated_source_repair: 'Multiple categories explain the imbalance',
};

const signedMoney = (value, currency = 'USD') => {
  if (!Number.isFinite(value)) return '—';
  const formatted = money(Math.abs(value), currency);
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatted}`;
};

/**
 * The always-visible journal result. It answers only the one question the
 * reviewer needs before deciding: did the source and journal disagree?
 * Raw source text, mapping gaps and audit provenance belong in the parent's
 * single Details disclosure.
 */
const JournalImbalanceCard = ({ artifact }) => {
  const data = artifact?.data;
  if (!data || !Number.isFinite(data.residual)) {
    return <div className="ji-card ji-card--empty">This run could not diagnose the day.</div>;
  }

  const { sourceDocument } = data;
  const source = sourceDocument?.explanation || null;
  // The source explanation outranks the arithmetic: both can find the same
  // line, but only the document can say the side is wrong rather than the
  // amount. When they agree, show the confirmed one.
  const confirmed = data.confirmedBySource === true;
  const coordinated = confirmed && data.coordinatedRepair?.correctionCount > 1
    ? {
        cause: 'coordinated_source_repair',
        confidence: 'high',
        explanation: data.coordinatedRepair.explanation,
      }
    : null;
  const primary = coordinated || source || (Array.isArray(data.causes) ? data.causes[0] : null);

  // A one-line mismatch reads well as a direct source-vs-journal comparison.
  // A coordinated repair is already itemized in the proposal list; repeating
  // nine raw comparisons here buries the actual decision.
  const entries = coordinated ? [] : (source?.entries || []);
  return (
    <div className="ji-card">
      <div className="ji-head">
        {confirmed
          ? <span className="ji-badge ji-badge--confirmed">Source confirmed</span>
          : primary && <span className="ji-badge">{primary.confidence || 'low'} confidence</span>}
        <span className="ji-eyebrow">{CAUSE_LABEL[primary?.cause] || 'Diagnosis'}</span>
      </div>

      {entries.length > 0 ? (
        <div className="ji-compare" aria-label="Night audit compared with journal">
          {entries.map((entry, i) => (
            <React.Fragment key={i}>
              <span>Night audit <strong>{signedMoney(entry.reportedAmount)}</strong>{entry.reportedSide ? ` ${entry.reportedSide}` : ''}</span>
              <span className="ji-compare-separator" aria-hidden="true">·</span>
              <span>Journal <strong>{signedMoney(entry.storedAmount)}</strong></span>
            </React.Fragment>
          ))}
        </div>
      ) : primary ? (
        <p className="ji-cause-text">{primary.explanation}</p>
      ) : null}

      {data.systemic?.kind === 'feed_convention' && (
        <div className="ji-warn">
          {data.systemic.daysMatching} of the last {data.systemic.daysChecked} days are out of balance the same way
          {data.systemic.repairStaged
            ? ' — the proposals below repair this day only.'
            : data.systemic.repairOffered
              ? ' — a one-day repair can be staged on request; the durable fix is at the feed.'
              : ' — no ledger entry type exists to carry a balancing line.'}
        </div>
      )}
      {!sourceDocument?.available && !sourceDocument?.skipped && (
        <div className="ji-warn">Source document unavailable; this diagnosis uses the journal only.</div>
      )}
    </div>
  );
};

/** Audit material for WorkflowRunCard's one shared disclosure. */
export const JournalImbalanceDetails = ({ artifact }) => {
  const data = artifact?.data || {};
  const { sourceDocument, unmapped, limitations = [] } = data;
  const source = sourceDocument?.explanation || null;
  const entries = source?.entries || [];
  const nonMappingLimitations = limitations.filter(l => !/unmapped revenue type/i.test(String(l)));

  return (
    <>
      {sourceDocument?.available && entries.length > 0 && (
        <div className="ji-detail-section">
          <div className="ji-section-title">Night-audit source</div>
          {entries.map((entry, i) => (
            <div className="ji-source-row" key={i}>
              <code className="ji-source-line">{entry.sourceLine}</code>
              <div className="ji-source-compare">
                Reported <strong>{signedMoney(entry.reportedAmount)}</strong>{entry.reportedSide ? ` ${entry.reportedSide}` : ''}
                {' · '}journal stored <strong>{signedMoney(entry.storedAmount)}</strong>
              </div>
            </div>
          ))}
        </div>
      )}

      {unmapped?.types?.length > 0 && (
        <div className="ji-detail-section">
          <div className="ji-section-title">Mapping gap</div>
          <div className="ji-unmapped-note">
            {unmapped.types.length} unmapped revenue type{unmapped.types.length === 1 ? '' : 's'}: {unmapped.types.join(', ')}.
            These are not the cause of the imbalance.
          </div>
        </div>
      )}

      {nonMappingLimitations.length > 0 && (
        <div className="ji-detail-section">
          <div className="ji-section-title">Limitations</div>
          <ul className="ji-limitations">
            {nonMappingLimitations.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </div>
      )}
    </>
  );
};

export default JournalImbalanceCard;
